import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  Crown, FileDown, Network, LogOut,
  TrendingUp, Landmark, MapPin, PieChart, Sparkles, ScrollText,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { PresidentialShell } from "./DirectorApply";
import {
  EcosystemTicker, BoardRoster, BoardMotionsPanel, FounderCountryBadge,
} from "../components/DirectorExtras";
import { FounderConciergeChat, useCurrencyToggle, CurrencyToggle } from "../components/FounderConcierge";
import { FounderPhotoUploader, ConciergeDot } from "../components/DirectorInvitesAndPhoto";
import { DirectorJourney } from "../components/journey/DirectorJourney";
import { RoleBadge } from "../components/journey/RoleBadge";

export default function DirectorPortal() {
  const { user, logout, api } = useAuth();
  const nav = useNavigate();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [status, setStatus] = useState(null);
  const currencyHook = useCurrencyToggle(api);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = (await api.get("/director/status")).data;
      setStatus(s);
      if (s.director_status !== "active") { setLoading(false); return; }
      const d = (await api.get("/director/dashboard")).data;
      setBoard(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not load Board data.");
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (user === false) nav("/login");
    else if (user && user.role !== "director" && user.role !== "admin") nav("/account");
  }, [user, nav]);

  const downloadReport = async (variant) => {
    setDownloading(variant);
    try {
      const path = variant === "executive" ? "/director/reports/executive.pdf"
        : variant === "network" ? "/director/reports/network.pdf"
        : variant === "chairman" ? "/director/reports/chairman-letter.pdf"
        : variant === "induction" ? "/director/reports/induction-certificate.pdf"
        : "/director/reports/shareholding-certificate.pdf";
      const r = await api.get(path, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.headers?.["content-disposition"]?.match(/filename="([^"]+)"/)?.[1]
        || `Vinci-${variant}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded under Digital Board Seal.");
    } catch (e) {
      toast.error("Could not generate the document.");
    } finally { setDownloading(null); }
  };

  const previewReport = async (variant) => {
    try {
      // Fetch inline PDF as a blob so cookies are attached, then open in a new tab.
      const map = { executive: "executive", network: "network",
                     chairman: "chairman", induction: "induction",
                     shareholding: "shareholding" };
      const kind = map[variant] || "induction";
      const r = await api.get(`/director/reports/${kind}/preview.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch { toast.error("Could not open preview."); }
  };

  const doLogout = async () => { await logout(); nav("/"); };

  if (loading) {
    return (
      <PresidentialShell>
        <div className="flex flex-col items-center justify-center gap-6 py-24">
          <div className="w-14 h-14 rounded-full border-2 border-[#C9A961]/30 border-t-[#C9A961] animate-spin" />
          <p className="font-grotesk text-xs uppercase tracking-[0.35em] text-[#C9A961]">
            Preparing your Founders Suite…
          </p>
        </div>
      </PresidentialShell>
    );
  }

  if (status && status.director_status !== "active") {
    return (
      <PresidentialShell>
        <div className="max-w-xl mx-auto text-center">
          <Crown size={40} className="mx-auto text-[#C9A961]" />
          <h1 className="font-serif text-3xl mt-6">Board Portal not yet unlocked</h1>
          <p className="font-body text-[#8A8A82] mt-4">
            Your Executive account is currently <span className="text-[#C9A961]">{status.director_status.replaceAll("_", " ")}</span>.
          </p>
          <Link to="/director/apply" data-testid="director-goto-apply" className="mt-8 inline-block rounded-sm bg-[#C9A961] text-black px-7 py-3 font-grotesk text-xs uppercase tracking-[0.28em] hover:bg-[#8B7333] hover:text-[#F5F5F0] transition-colors">
            Resume application →
          </Link>
        </div>
      </PresidentialShell>
    );
  }

  if (!board) return null;

  const share = board.director?.shareholding_pct || 0;

  return (
    <PresidentialShell elite>
      <Toaster position="bottom-right" theme="dark" richColors />
      <ConciergeDot api={api} />

      {/* Elite Hero */}
      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
        className="relative mb-8"
      >
        <div className="flex flex-wrap items-start justify-between gap-6 mb-8">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <motion.div
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              >
                <Crown size={26} className="text-[#C9A961]" />
              </motion.div>
              <span className="font-grotesk text-[10px] uppercase tracking-[0.4em] text-[#C9A961]">
                · Executive Board · Founders Suite · Level 0 ·
              </span>
              <FounderCountryBadge country={board.director?.country} state={board.director?.state_or_region} />
              {status?.role && <RoleBadge role={status.role} size="md" pulse={!!status?.role_pulse_pending} />}
            </div>
            <ShimmerHeading>Welcome, {board.director?.name || user?.email?.split("@")[0]}</ShimmerHeading>
            <p className="font-body text-sm md:text-base text-[#C9C9C2]/80 mt-4 max-w-2xl leading-relaxed">
              Presidential access is live. Every ledger, every custodian, every
              elite Big-Cap tender — laid out for the Board under the Digital
              Vinci Seal.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <EliteButton
              testid="director-download-executive"
              onClick={() => downloadReport("executive")}
              disabled={downloading === "executive"}
              primary
            >
              <FileDown size={16} />
              {downloading === "executive" ? "Rendering…" : "Executive Report"}
            </EliteButton>
            <EliteButton
              testid="director-download-network"
              onClick={() => downloadReport("network")}
              disabled={downloading === "network"}
            >
              <Network size={16} />
              {downloading === "network" ? "Rendering…" : "Network Performance"}
            </EliteButton>
            <EliteButton
              testid="director-download-cert"
              onClick={() => downloadReport("shareholding")}
              disabled={downloading === "shareholding"}
            >
              <ScrollText size={16} />
              {downloading === "shareholding" ? "Rendering…" : "Cap-Table Cert"}
            </EliteButton>
            <EliteButton
              testid="director-download-letter"
              onClick={() => downloadReport("chairman")}
              disabled={downloading === "chairman"}
            >
              <ScrollText size={16} />
              {downloading === "chairman" ? "Rendering…" : "Chairman's Letter"}
            </EliteButton>
            <EliteButton
              testid="director-download-induction"
              onClick={() => downloadReport("induction")}
              disabled={downloading === "induction"}
              primary
            >
              <ScrollText size={16} />
              {downloading === "induction" ? "Rendering…" : "Official Induction Certificate"}
            </EliteButton>
            <EliteButton testid="director-preview-induction" onClick={() => previewReport("induction")} muted>
              Preview ↗
            </EliteButton>
            <EliteButton testid="director-logout" onClick={doLogout} muted>
              <LogOut size={14} /> Sign out
            </EliteButton>
          </div>
        </div>
        {/* USD / INR toggle + Photo uploader */}
        <div className="flex justify-between flex-wrap gap-4 mt-4">
          <FounderPhotoUploader api={api} director={board.director} onDone={load} />
          <CurrencyToggle {...currencyHook} />
        </div>
      </motion.div>

      {/* Live Ticker */}
      <EcosystemTicker api={api} />

      {/* Cinematic onboarding journey + pending celebration */}
      <DirectorJourney api={api} />

      {/* Shareholding Certificate — the glow piece */}
      <ShareholdingHero share={share} director={board.director} totalRevenue={board.total_revenue} />

      {/* Board Roster with live cap-table pie */}
      <div className="mt-14">
        <BoardRoster api={api} />
      </div>

      {/* Board Motions module */}
      <BoardMotionsPanel api={api} />

      {/* Founder Concierge — private inbox */}
      <FounderConciergeChat api={api} founder={board.director} />

      {/* KPI Grid */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.9 }}
        data-testid="director-kpi-grid"
        className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16 mb-16"
      >
        {board.kpis.map(([label, value], i) => {
          // Server-formatted values may look like "₹6.65 L" or "4". Only
          // convert when a raw rupee number is present (contains "₹").
          const converted = (currencyHook.currency === "USD" && value.includes("₹"))
            ? _convertToUsd(value, currencyHook.fx.inr_per_usd)
            : value;
          return (
          <motion.div
            key={label}
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 + 0.06 * i, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            whileHover={{ y: -3, transition: { duration: 0.2 } }}
            className="group relative rounded-sm border border-[#C9A961]/25 bg-gradient-to-br from-[#0f0d0a] via-[#0b0908] to-[#0f0d0a] p-6 overflow-hidden"
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                 style={{ background: "linear-gradient(135deg,rgba(201,169,97,0.12),transparent 60%)" }} />
            <div className="absolute -right-8 -bottom-8 w-24 h-24 rounded-full blur-2xl opacity-20 group-hover:opacity-40 transition-opacity" style={{ background: "#C9A961" }} />
            <div className="relative">
              <div className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#C9A961]">{label}</div>
              <div className="font-serif text-3xl md:text-4xl text-[#F5F5F0] mt-3 tabular-nums">{converted}</div>
            </div>
          </motion.div>
        );})}
      </motion.div>

      <div className="grid lg:grid-cols-3 gap-6 mb-16">
        <ElitePanel className="lg:col-span-2" testid="director-revenue-panel">
          <PanelHeader kicker="· Master Ledger ·" title="Revenue timeline · last 6 months" Icon={TrendingUp} />
          <RevenueChart data={board.revenue_series} />
        </ElitePanel>

        <ElitePanel testid="director-claim-funnel">
          <PanelHeader kicker="· Iron-Clad Recovery ·" title="Claim funnel" Icon={Sparkles} />
          <div className="space-y-4 mt-4">
            {board.claim_funnel.map(([stage, n], i) => {
              const max = Math.max(1, ...board.claim_funnel.map((r) => r[1]));
              return (
                <div key={stage}>
                  <div className="flex justify-between font-body text-sm mb-1.5">
                    <span className="text-[#C9C9C2]">{stage}</span>
                    <span className="text-[#C9A961] tabular-nums font-serif text-base">{n}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }} animate={{ width: `${Math.min(100, (n / max) * 100)}%` }}
                      transition={{ delay: 0.6 + i * 0.08, duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
                      className="h-full rounded-full"
                      style={{ background: "linear-gradient(90deg,#C9A961 0%,#8B7333 100%)" }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </ElitePanel>
      </div>

      {/* Ecosystem Roster */}
      <ElitePanel className="mb-16" testid="director-ecosystem">
        <PanelHeader
          kicker="· Ecosystem Roster · aggregated & masked ·"
          title="Partner Distribution"
          Icon={Landmark}
        />
        <EliteTable
          headers={["Tier", "Active", "Pending", "Rejected", "Total"]}
          rows={board.ecosystem}
        />
      </ElitePanel>

      {/* Big-Cap Full Visibility */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="relative rounded-sm border-2 border-[#C9A961]/40 bg-gradient-to-br from-[#141008] via-[#0f0d0a] to-[#0a0806] p-8 mb-16 overflow-hidden"
        data-testid="director-bigcap"
      >
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-[100px] opacity-30" style={{ background: "#C9A961" }} />
        <div className="relative">
          <PanelHeader
            kicker="· Big-Cap League · Full Visibility ·"
            title="Elite Infrastructure Roster"
            Icon={Crown}
          />
          {board.big_cap.length === 0 ? (
            <p className="font-body text-[#8A8A82] italic mt-4">No Big-Cap applications yet.</p>
          ) : (
            <EliteTable
              headers={["Business", "State", "Turnover (₹)", "Trades", "Status"]}
              rows={board.big_cap}
            />
          )}
        </div>
      </motion.div>

      {/* State Grid */}
      <ElitePanel className="mb-16" testid="director-state-grid">
        <PanelHeader
          kicker="· 20-State Posture ·"
          title="Custodian Coverage & Supplier Grid"
          Icon={MapPin}
        />
        <EliteTable
          headers={["State", "Custodians", "Suppliers", "Active Buyers", "GMV (₹)"]}
          rows={board.state_grid}
        />
      </ElitePanel>

      <p className="font-body text-xs text-[#8A8A82] text-center mt-12 mb-8 italic max-w-2xl mx-auto">
        This portal shows aggregated ecosystem data. PII of non-Big-Cap partners
        is masked per Board policy. Only Admin can authorize, revoke or adjust
        Executive Board access & shareholding.
      </p>
    </PresidentialShell>
  );
}

/* ─── Elite Building Blocks ────────────────────────────────── */

function ShimmerHeading({ children }) {
  return (
    <h1
      className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight relative"
      style={{
        background: "linear-gradient(120deg, #F5F5F0 0%, #C9A961 40%, #F5F5F0 60%, #C9A961 100%)",
        backgroundSize: "200% auto",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        WebkitTextFillColor: "transparent",
        animation: "shimmerFlow 8s linear infinite",
      }}
    >
      {children}
      <style>{`@keyframes shimmerFlow { 0% { background-position: 0% center; } 100% { background-position: 200% center; } }`}</style>
    </h1>
  );
}

function EliteButton({ children, primary, muted, testid, onClick, disabled }) {
  const base = "inline-flex items-center gap-2 rounded-sm px-6 py-3 font-grotesk text-[10px] uppercase tracking-[0.28em] transition-all disabled:opacity-60 relative overflow-hidden group";
  const style = primary
    ? "bg-gradient-to-r from-[#C9A961] to-[#8B7333] text-black hover:from-[#8B7333] hover:to-[#C9A961] hover:text-[#F5F5F0] shadow-[0_0_30px_rgba(201,169,97,0.25)]"
    : muted
    ? "border border-white/15 text-[#C9C9C2] hover:border-[#C0392B] hover:text-[#C0392B]"
    : "border border-[#C9A961]/60 text-[#C9A961] hover:bg-[#C9A961] hover:text-black";
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled} className={`${base} ${style}`}>
      <span className="relative z-10 flex items-center gap-2">{children}</span>
    </button>
  );
}

function ElitePanel({ children, className = "", testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7 }}
      data-testid={testid}
      className={`rounded-sm border border-[#C9A961]/20 bg-gradient-to-br from-[#0f0d0a] to-[#0a0806] p-8 relative overflow-hidden ${className}`}
    >
      <div className="absolute top-0 right-0 w-32 h-px" style={{ background: "linear-gradient(90deg, transparent, #C9A961, transparent)" }} />
      {children}
    </motion.div>
  );
}

function PanelHeader({ kicker, title, Icon }) {
  return (
    <div className="flex items-center justify-between mb-6">
      <div>
        <span className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#C9A961]">{kicker}</span>
        <h2 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-2">{title}</h2>
      </div>
      {Icon && <Icon className="text-[#C9A961]" size={26} />}
    </div>
  );
}

function EliteTable({ headers, rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-[#C9A961]/30">
            {headers.map((h) => (
              <th key={h} className="font-grotesk text-[10px] uppercase tracking-[0.25em] text-[#C9A961] py-3 pr-6">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <motion.tr
              key={i}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.03 }}
              className="border-b border-white/[0.05] hover:bg-[#C9A961]/[0.04] transition-colors"
            >
              {row.map((c, j) => (
                <td key={j} className={`py-3 pr-6 font-body text-sm ${j === 0 ? "text-[#F5F5F0]" : "tabular-nums text-[#C9C9C2]"}`}>{c}</td>
              ))}
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RevenueChart({ data }) {
  const max = Math.max(1, ...data.map(([, v]) => v));  return (
    <div className="flex items-end gap-4 h-56 mt-2" data-testid="director-revenue-chart">
      {data.map(([label, v], i) => {
        const h = (v / max) * 100;
        return (
          <div key={label} className="flex-1 flex flex-col items-center justify-end group">
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: `${h}%` }}
              transition={{ duration: 1.1, delay: 0.1 * i, ease: [0.16, 1, 0.3, 1] }}
              className="w-full rounded-t-sm relative group-hover:brightness-125 transition-all"
              style={{ background: "linear-gradient(180deg,#C9A961 0%, #8B7333 100%)", boxShadow: "0 0 24px rgba(201,169,97,0.35)" }}
            />
            <div className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#C9A961] mt-3">{label}</div>
            <div className="font-body text-[10px] text-[#8A8A82] tabular-nums mt-1">
              ₹{(v / 1e5).toFixed(1)}L
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Helpers ────────────────────────────────── */
function _convertToUsd(v, rate) {
  const m = /₹\s*([\d.,]+)\s*(Cr|L)?/i.exec(v);
  if (!m) return v;
  let num = parseFloat(m[1].replace(/,/g, ""));
  if (m[2]) num *= (m[2].toLowerCase() === "cr" ? 1e7 : 1e5);
  const usd = num / (rate || 83);
  if (usd >= 1e6) return `$${(usd/1e6).toFixed(2)}M`;
  if (usd >= 1e3) return `$${(usd/1e3).toFixed(2)}K`;
  return `$${usd.toFixed(0)}`;
}

/* ─── Shareholding Hero ────────────────────────────────────── */

function ShareholdingHero({ share, director, totalRevenue }) {
  const [display, setDisplay] = useState(0);
  const size = 260;
  const R = 110;
  const C = 2 * Math.PI * R;
  const dash = (share / 100) * C;

  useEffect(() => {
    let raf, start = 0;
    const t0 = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - t0) / 1400);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(share * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [share]);

  const notional = ((totalRevenue || 0) * share) / 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
      className="relative rounded-sm border-2 border-[#C9A961]/40 overflow-hidden"
      data-testid="director-shareholding"
    >
      {/* Multi-layer gold glow backdrop */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1a1408] via-[#0f0b06] to-[#0a0806]" />
      <div className="absolute inset-0 opacity-40">
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-96 h-96 rounded-full blur-[110px]"
             style={{ background: "radial-gradient(circle,#C9A961 0%,transparent 65%)" }} />
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-96 h-96 rounded-full blur-[110px]"
             style={{ background: "radial-gradient(circle,#8B7333 0%,transparent 65%)" }} />
      </div>
      {/* Marble streaks */}
      <div className="absolute inset-0 opacity-[0.05] mix-blend-overlay"
           style={{ backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22400%22 height=%22400%22><filter id=%22n%22><feTurbulence baseFrequency=%220.8%22 numOctaves=%223%22/><feColorMatrix values=%22.9 .8 .3 0 0 .9 .7 .2 0 0 .7 .5 .1 0 0 0 0 0 1 0%22/></filter><rect width=%22400%22 height=%22400%22 filter=%22url(%23n)%22/></svg>')" }} />

      <div className="relative p-8 md:p-12 grid md:grid-cols-2 gap-10 items-center">
        {/* Left — headline */}
        <div>
          <div className="flex items-center gap-2 mb-6">
            <PieChart size={18} className="text-[#C9A961]" />
            <span className="font-grotesk text-[10px] uppercase tracking-[0.35em] text-[#C9A961]">
              · Founders Charter · Shareholding Certificate ·
            </span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[#F5F5F0]">
            Your equity stake in
            <br />
            <span style={{
              background: "linear-gradient(120deg,#C9A961,#F5F5F0,#C9A961)",
              backgroundSize: "200% auto",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              animation: "shimmerFlow 6s linear infinite",
            }}>
              Vinci Inc. India
            </span>
          </h2>
          <p className="font-body text-[#C9C9C2]/80 mt-5 leading-relaxed max-w-md">
            Recorded on the Vinci cap-table under the Digital Board Seal.
            Only the Board Secretariat (Admin) can revise this figure.
          </p>

          <div className="mt-8 grid grid-cols-2 gap-4 max-w-sm">
            <ShareStat label="Notional GMV Share" value={notional > 0
              ? (notional >= 1e7 ? `₹${(notional/1e7).toFixed(2)} Cr` : `₹${(notional/1e5).toFixed(2)} L`)
              : "₹0"} />
            <ShareStat label="Vinci Code" value={`#${(director?.id || "").slice(-6).toUpperCase() || "—"}`} />
          </div>
        </div>

        {/* Right — glowing radial gauge */}
        <div className="flex items-center justify-center">
          <div className="relative" style={{ width: size, height: size }}>
            {/* Pulse aura */}
            <motion.div
              animate={{ scale: [1, 1.06, 1], opacity: [0.35, 0.6, 0.35] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 rounded-full"
              style={{ background: "radial-gradient(circle,#C9A961 0%,transparent 70%)", filter: "blur(30px)" }}
            />
            <svg width={size} height={size} className="relative" style={{ filter: "drop-shadow(0 0 20px rgba(201,169,97,0.5))" }}>
              <defs>
                <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#F5D67A" />
                  <stop offset="50%" stopColor="#C9A961" />
                  <stop offset="100%" stopColor="#8B7333" />
                </linearGradient>
              </defs>
              {/* Track */}
              <circle cx={size/2} cy={size/2} r={R} fill="none" stroke="rgba(201,169,97,0.12)" strokeWidth="14" />
              {/* Inner rings */}
              <circle cx={size/2} cy={size/2} r={R - 22} fill="none" stroke="rgba(201,169,97,0.15)" strokeWidth="0.6" />
              <circle cx={size/2} cy={size/2} r={R + 12} fill="none" stroke="rgba(201,169,97,0.2)" strokeWidth="0.6" />
              {/* Progress */}
              <motion.circle
                cx={size/2} cy={size/2} r={R}
                fill="none"
                stroke="url(#ringGradient)"
                strokeWidth="14"
                strokeLinecap="round"
                strokeDasharray={C}
                initial={{ strokeDashoffset: C }}
                animate={{ strokeDashoffset: C - dash }}
                transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
                transform={`rotate(-90 ${size/2} ${size/2})`}
              />
              {/* Tick marks */}
              {Array.from({length: 24}).map((_, i) => {
                const angle = (i * 15 - 90) * Math.PI/180;
                const x1 = size/2 + Math.cos(angle) * (R + 24);
                const y1 = size/2 + Math.sin(angle) * (R + 24);
                const x2 = size/2 + Math.cos(angle) * (R + 30);
                const y2 = size/2 + Math.sin(angle) * (R + 30);
                return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#C9A961" strokeOpacity={i % 6 === 0 ? 0.6 : 0.25} strokeWidth={i % 6 === 0 ? 1.2 : 0.6} />;
              })}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-grotesk text-[9px] uppercase tracking-[0.35em] text-[#C9A961]">Equity</span>
              <div className="font-serif text-6xl md:text-7xl text-[#F5F5F0] tabular-nums leading-none mt-1"
                   data-testid="director-share-value">
                {display.toFixed(2).replace(/\.00$/, "")}<span className="text-[#C9A961]">%</span>
              </div>
              <span className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#8A8A82] mt-2">of Vinci Inc.</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom ledger strip */}
      <div className="relative border-t border-[#C9A961]/25 px-8 md:px-12 py-4 flex flex-wrap items-center justify-between gap-4 bg-black/40">
        <span className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#8A8A82]">
          Board Certified · Vinci Inc. India · MMXXVI
        </span>
        <span className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#C9A961]">
          Certified holder: {director?.name || director?.email}
        </span>
      </div>
    </motion.div>
  );
}

function ShareStat({ label, value }) {
  return (
    <div className="border-l-2 border-[#C9A961]/40 pl-4">
      <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#C9A961]">{label}</div>
      <div className="font-serif text-xl text-[#F5F5F0] mt-1 tabular-nums">{value}</div>
    </div>
  );
}
